export const copilotChatTextAreaId = 'copilot-chat-textarea'
export const copilotChatSearchInputId = 'copilot-chat-topic-search'
export const reviewUserMessage = 'Review'
export const copilotChatHeaderButtonID = 'copilot-chat-header-button'
export const copilotDiffHeaderButtonID = 'copilot-diff-header-button'
export const copilotChatPanelID = 'copilot-chat-panel'
export const copilotChatPanelInnerID = 'copilot-chat-panel'
export const copilotDockedPanelID = 'copilot-chat-docked-content'
export const copilotDockedPanelHeaderID = 'copilot-chat-docked-header'
export const copilotDockedPanelReadyEvent = 'copilot-docked-panel:ready'
export const copilotDockedPanelClosedEvent = 'copilot-docked-panel:closed'
/**
 * Dispatched on mount by a docked-capable page while the docked panel itself is
 * still closed, marking the page as one where copilot chat lives in the docked
 * side panel rather than a floating panel. Distinct from
 * `copilotDockedPanelReadyEvent`, which fires when the docked panel *opens* and
 * is also consumed by the immersive chat. Only the embedded chat v2 surface
 * listens to this page-level signal, so it can suppress its floating panel on a
 * hard refresh (where the docked panel body isn't mounted yet) without altering
 * immersive/header behavior. See github/copilot-experiences#12678.
 */
export const copilotDockedPageReadyEvent = 'copilot-docked-page:ready'
/**
 * Dispatched when a docked-capable page is unmounted (SPA navigation away).
 * Distinct from `copilotDockedPanelClosedEvent`, which fires when just the
 * side panel closes on the same docked page. Consumers use this to reset
 * cross-page state (e.g. release module-level claims, hide docked UI).
 */
export const copilotDockedPageEndedEvent = 'copilot-docked-page:ended'

/**
 * Tracks which UI has claimed the docked panel target on this page.
 * Once claimed, the other UI must not portal into it.
 * Reset on full page navigation (module re-evaluation).
 */
type DockedPanelOwner = 'immersive' | 'chatapp'

let dockedPanelClaimedBy: DockedPanelOwner | null = null

export function claimDockedPanel(owner: DockedPanelOwner): boolean {
  if (dockedPanelClaimedBy === null) {
    dockedPanelClaimedBy = owner
    return true
  }
  return dockedPanelClaimedBy === owner
}

export function getDockedPanelOwner(): DockedPanelOwner | null {
  return dockedPanelClaimedBy
}

export function transferDockedPanelClaim(from: DockedPanelOwner, to: DockedPanelOwner): boolean {
  if (dockedPanelClaimedBy !== from) return false
  dockedPanelClaimedBy = to
  return true
}

export function getOpenCopilotChatVersion({
  chatAppEmbeddedEnabled,
  source,
}: {
  chatAppEmbeddedEnabled: boolean
  source?: string
}): 'v1' | 'v2' {
  if (dockedPanelClaimedBy === 'chatapp') return 'v2'
  if (dockedPanelClaimedBy === 'immersive') return 'v1'
  return chatAppEmbeddedEnabled && source !== 'header' ? 'v2' : 'v1'
}

/** Release the docked panel claim so a different UI can claim it next. */
export function releaseDockedPanel(owner: DockedPanelOwner): void {
  if (dockedPanelClaimedBy === owner) {
    dockedPanelClaimedBy = null
  }
}
